import { parse } from 'qs';

import { localStorage } from '@sorare/core/src/lib/storage/localStorage';
import type { SessionStorageEntries } from '@sorare/core/src/lib/storage/sessionStorage';
import { sessionStorage } from '@sorare/core/src/lib/storage/sessionStorage';

import {
  canPersistSessionStorageParam,
  queryParamsToLocalStorageKeys,
  queryParamsToSessionStorageKeys,
} from './storage.ts';

// oxlint-disable-next-line no-restricted-properties
const searchParams = parse(window.location.search, {
  ignoreQueryPrefix: true,
  decoder(value) {
    if (/^(\d+|\d*\.\d+)$/.test(value)) {
      return parseFloat(value);
    }
    const keywords: Record<string, boolean | null | undefined> = {
      true: true,
      false: false,
      null: null,
      undefined,
    };
    if (value in keywords) {
      return keywords[value];
    }
    return value;
  },
});

// Duplicated query params (`?referrer=x&referrer=x`) parse to an array; left as-is
// it round-trips through storage and reaches String-typed mutation inputs as an array.
const firstScalar = (value: unknown): unknown =>
  Array.isArray(value) ? firstScalar(value[0]) : value;

const LOCAL_STORAGE_ENTRIES = Object.entries(queryParamsToLocalStorageKeys);
// if any UTM params are present, clear former ones from local storage first to avoid merging them together
if (LOCAL_STORAGE_ENTRIES.some(([qs]) => searchParams[qs])) {
  LOCAL_STORAGE_ENTRIES.forEach(([, lsKey]) => localStorage.removeItem(lsKey));
}
LOCAL_STORAGE_ENTRIES.forEach(([qsKey, lskey]) => {
  const value = firstScalar(searchParams[qsKey]);
  if (value) {
    localStorage.setItem(lskey, String(value));
  }
});
// The decoder above skips percent-decoding, and the TWA percent-encodes the `:`
// in FCM tokens; the backend needs the decoded token.
// oxlint-disable-next-line no-restricted-properties
const fcmToken = new URLSearchParams(window.location.search).get('fcmToken');
const decodedSearchParams: typeof searchParams = {
  ...searchParams,
  fcmToken: fcmToken ?? undefined,
};

Object.entries(queryParamsToSessionStorageKeys).forEach(([qsKey, sskey]) => {
  const value = decodedSearchParams[qsKey];
  if (value === undefined) {
    return;
  }
  if (!canPersistSessionStorageParam(qsKey, document.referrer)) {
    return;
  }
  sessionStorage.setItem(sskey, value as SessionStorageEntries[typeof sskey]);
});
